import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useQuery, useMutation } from "@tanstack/react-query";

import { getBasketData, refetchBasketData } from "../queries/global";
import {
  NotificationContext,
  handleError,
} from "../context/NotificationContext";

import config from "../config/config";
import * as QUERIES from "../queries/query-functions";
import * as UTILS from "../utils/utils";

import InstanceEventSummary from "../components/instance-event-summary";
import TicketQuantityButtons from "../components/ticket-quantity-buttons";
import LoadingIndicator from "../components/loading-indicator";
import CheckForQueue from "./check-for-queue";
import CheckForPriority from "./check-for-priority";
import PreviousIcon from "../svg/previous";
import BestAvailable from "./best-available";
import TicketStepIcon from "../svg/ticket-step";
import { useMediaQuery } from "../hook/useMediaQuery";
import SeatSelectionSummary from "./seat-selection-summary";
import PromoCodeInput from "../components/promo-code-input";
import TransactionCommissionMessage from "../components/transaction-commission-message";

let unreserved_ticketType_ID = null;
const advisory_message = config.OPTION_FIELDS.booking_advisory_message;
const general_admission_message = config.OPTION_FIELDS.general_admission_message?.innerHTML ?? null

export default function BookOnline(props) {
  const { notification, setNotification } = useContext(NotificationContext);
  const { data: basketData, isFetching: basketDataFetching } = getBasketData(); 
  const isMobile = useMediaQuery(`(max-width: 768px)`);
  const dialogRef = useRef(null);

  const { instanceId } = useParams();
  let navigate = useNavigate();

  const [isReserved, setIsReserved] = useState(null);
  const [areaTicketQuantities, setAreaTicketQuantities] = useState(null);
  const [ticketQuantity, setTicketQuantity] = useState(1);
  const [bandsByPrice, setBandsByPrice] = useState(null);
  const [stepNumber, setStepNumber] = useState('one');
  const [ticketsTotal, setTicketsTotal] = useState(null);

  const addTickets = useMutation({
    mutationFn: ({ quantity, id, planID }) =>
      QUERIES.addTickets(quantity, id, null, config.INSTANCE_ID, planID),
    onError: (error) => {
      handleError(error.message, setNotification);
    },
    onSuccess: (data) => {
      // navigate(props.continueLink);
      refetchBasketData();
    },
  });

  const { data: planData } = useQuery({
    queryKey: ["planData", config.PLAN_ID],
    queryFn: QUERIES.getPlanData,
    staleTime: 86400000,
    refetchOnWindowFocus: false,
  });

  const { data: priceListData } = useQuery({
    queryKey: ["priceListData", config.INSTANCE_ID],
    queryFn: () => QUERIES.getInstancePriceList(config.INSTANCE_ID),
    staleTime: 86400000,
    refetchOnWindowFocus: false,
    enabled: !isReserved,
  });

  const handleAddTickets = () => {
    if (areaTicketQuantities?.length) {
      areaTicketQuantities.forEach((area) => {
        if (!area.quantity) return;
        addTickets.mutateAsync({
          quantity: area.quantity,
          id: area.ticket_id,
          planID: area.plan_id,
        });
      });
    } else {
      if (!ticketQuantity) return;
      addTickets.mutateAsync({
        quantity: ticketQuantity,
        id: unreserved_ticketType_ID,
        planID: config.PLAN_ID,
      });
    }
  };

  useEffect(() => {
    if (!priceListData || !props.checkedForQueue) return;

    // Get the band id's from bandData and add it to bandsByPrice to be used by addBestAvailable()
    const priceListDataBandDefault = priceListData.filter(item => item.isBandDefault)
    const updatedBandsArray =  UTILS.getBandsByPrice(priceListDataBandDefault).sort((a, b) => a.amount - b.amount );

    setBandsByPrice(updatedBandsArray);
  }, [priceListData, props.checkedForQueue]);

  useEffect(() => {
    if (!planData || !props.checkedForQueue) return;

    const hasReserved = (area) => {
      if (area.type === "Reserved") return true;

      // If it's a Group check its nested areas
      if (area.type === "Group" && Array.isArray(area.areas)) {
        return area.areas.some(hasReserved);
      }
      return false;
    };

    const has_reserved_seating =
      hasReserved(planData) ||
      (Array.isArray(planData.areas) && planData.areas.some(hasReserved));

    if (!has_reserved_seating) {
      setIsReserved(false);

      if (priceListData) {
        // Check for priceband that is unreserved and the default ticket type
        let default_ticket = priceListData.find(
          (price) =>
            price.priceBand.name == "Unreserved" &&
            config.OPTION_FIELDS.default_ticket_type_name.includes(price.ticketType?.name)
        );

        if (!default_ticket) {
          // If "Unreserved" name doesn't exist just look for default ticket type
          default_ticket = priceListData.find(
            (price) =>
              config.OPTION_FIELDS.default_ticket_type_name.includes(price.ticketType?.name)
          );
        }

        // If no default ticket type just return the first in the array of prices
        unreserved_ticketType_ID = default_ticket
          ? default_ticket.ticketType?.id
          : priceListData[0].ticketType?.id;
      }

      // If plan has unreserved sub areas show them here
      if (planData.areas?.length) {
        setAreaTicketQuantities(() => {
          const areas = planData?.areas ?? [];

          return areas.map((area) => {
            const default_ticket = priceListData?.prices?.find(
              (price) => price.priceBand.name == area.name
            );

            return {
              name: area.name,
              quantity: 0,
              amount: default_ticket ? default_ticket.amount : null,
              ticket_id: default_ticket ? default_ticket.ticketType?.id : null,
              plan_id: area.id,
            };
          });
        });
      }
    } else {
      setIsReserved(true);

      if (!config.HAS_BEST_AVAILABLE) {
        navigate(`/book-online/${instanceId}/select-seats`);
      }
    }
  }, [planData, priceListData, props.checkedForQueue]);

  const removeTicket = useMutation({
    mutationFn: (ticketsIdArray) => QUERIES.removeTickets(ticketsIdArray),
      onError: (error) => {
        handleError(error.message, setNotification);
      },
      onSuccess: () => {
        refetchBasketData();
      },
  })

  const clearBasket = async () => {
    const tickets = basketData?.tickets || [];
    const ticketsIdArray = UTILS.getTicketsByInstance(tickets).map(t => t.id);
      if (ticketsIdArray.length) {
        await removeTicket.mutateAsync(ticketsIdArray);
      }
  }

  useEffect(() => {
    if (!basketData || !basketData?.tickets) {
      setTicketsTotal(null);
      return;
    }

    let tickets = UTILS.getTicketsByInstance(basketData.tickets);
    const tickets_total = UTILS.getTicketsTotal(tickets);

    setTicketsTotal(tickets_total ? tickets_total.toFixed(2) : null);
  }, [basketData])

  // Check to see if this event needs a queue by rendering the chooseseats iframe.
  // If the iframe goes to chooseSeats.aspx load the rest of the component
  if (!props.checkedForQueue) {
    return (
      <div className="booking-pathway__book-online">
        <LoadingIndicator type="page" />
        <CheckForQueue setCheckedForQueue={props.setCheckedForQueue} />
      </div>
    );
  }

  if (isReserved == null) return <LoadingIndicator type="page" />;

  return (
    <div className="booking-pathway__container booking-pathway__best-seat">
      {!props.checkedForPriority && (
        <CheckForPriority setCheckedForPriority={props.setCheckedForPriority} />
      )}

      <InstanceEventSummary showAlternativeDates="true" isReserved = {isReserved} />
            
      <section className="booking-pathway__main-content">
        <div className="booking-pathway__heading">
          {isReserved ?
            <h1 className="booking-pathway__header">Find the best seats</h1>
            : <> 
                <h1 className="booking-pathway__header">Select tickets</h1>
                {general_admission_message && <div className="booking-pathway__heading-text" dangerouslySetInnerHTML={{__html: general_admission_message}}></div>}
              </>
          }
        </div>

        <div className={`booking-pathway__select-quantity ${'booking-pathway__select-quantity-step--' + stepNumber} ${!isReserved && 'booking-pathway__select-quantity--no-reserved'}` }>
          {/* need to check */}
          {!config.HAS_BEST_AVAILABLE && areaTicketQuantities?.length ? (
            areaTicketQuantities.map((area) => (
              <div
                key={area.name}
                className="booking-pathway__ticket-quantity booking-pathway__ticket-quantity--multiple"
              >
                <h2 className="booking-pathway__header">
                  {area.name}
                  {area.amount && ` - £${parseFloat(area.amount).toFixed(2)}`}
                </h2>
                <TicketQuantityButtons
                  ticketQuantity={area.quantity}
                  setTicketQuantity={(newQty) => {
                    setAreaTicketQuantities((prev) =>
                      prev.map((item) =>
                        item.name === area.name
                          ? { ...item, quantity: newQty }
                          : item
                      )
                    );
                  }}
                />
              </div>
            ))
          ) : (
            <>
              <span className="ticket-step-icon"><TicketStepIcon/><TicketStepIcon/><TicketStepIcon/></span>
              <div className="booking-pathway__ticket-quantity booking-pathway__ticket-quantity--single">
                <span className="ticket-step-icon"><TicketStepIcon/></span>
                {/* <span className="booking-pathway__progress-line">
                  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="16.0122" cy="16.0216" r="5.8064" transform="rotate(15.9306 16.0122 16.0216)" fill="white"/><path d="M15.9993 18.6999L18.1993 20.4333C18.3993 20.5888 18.5993 20.5944 18.7993 20.4499C18.9993 20.3055 19.066 20.111 18.9993 19.8666L18.1327 17.1333L20.3327 15.2999C20.5105 15.1444 20.566 14.9555 20.4993 14.7333C20.4327 14.511 20.2771 14.3999 20.0327 14.3999H17.3993L16.466 11.6999C16.3771 11.4777 16.2216 11.3666 15.9993 11.3666C15.7771 11.3666 15.6216 11.4777 15.5327 11.6999L14.5993 14.3999H11.966C11.7216 14.3999 11.566 14.511 11.4993 14.7333C11.4327 14.9555 11.4882 15.1444 11.666 15.2999L13.866 17.1333L12.9993 19.8666C12.9327 20.111 12.9993 20.3055 13.1993 20.4499C13.3993 20.5944 13.5993 20.5888 13.7993 20.4333L15.9993 18.6999ZM4.66602 26.6666C4.11046 26.6666 3.63824 26.4721 3.24935 26.0833C2.86046 25.6944 2.66602 25.2221 2.66602 24.6666L2.66602 20.2666C2.66602 20.0444 2.72713 19.8499 2.84935 19.6833C2.97157 19.5166 3.13268 19.3999 3.33268 19.3333C4.02157 19.0888 4.56046 18.6499 4.94935 18.0166C5.33824 17.3833 5.53268 16.711 5.53268 15.9999C5.53268 15.311 5.33824 14.6444 4.94935 13.9999C4.56046 13.3555 4.02157 12.911 3.33268 12.6666C3.13268 12.5999 2.97157 12.4833 2.84935 12.3166C2.72713 12.1499 2.66602 11.9555 2.66602 11.7333L2.66602 7.33325C2.66602 6.7777 2.86046 6.30547 3.24935 5.91659C3.63824 5.5277 4.11046 5.33325 4.66602 5.33325L27.3327 5.33325C27.8882 5.33325 28.3605 5.5277 28.7493 5.91659C29.1382 6.30547 29.3327 6.7777 29.3327 7.33325V11.7333C29.3327 11.9555 29.2716 12.1499 29.1493 12.3166C29.0271 12.4833 28.866 12.5999 28.666 12.6666C27.9771 12.911 27.4382 13.3555 27.0493 13.9999C26.6605 14.6444 26.466 15.311 26.466 15.9999C26.466 16.711 26.6605 17.3833 27.0493 18.0166C27.4382 18.6499 27.9771 19.0888 28.666 19.3333C28.866 19.3999 29.0271 19.5166 29.1493 19.6833C29.2716 19.8499 29.3327 20.0444 29.3327 20.2666V24.6666C29.3327 25.2221 29.1382 25.6944 28.7493 26.0833C28.3605 26.4721 27.8882 26.6666 27.3327 26.6666L4.66602 26.6666Z" fill="#B80E80"/></svg>
                  <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="16.0122" cy="16.0216" r="5.8064" transform="rotate(15.9306 16.0122 16.0216)" fill="white"/><path d="M15.9993 18.6999L18.1993 20.4333C18.3993 20.5888 18.5993 20.5944 18.7993 20.4499C18.9993 20.3055 19.066 20.111 18.9993 19.8666L18.1327 17.1333L20.3327 15.2999C20.5105 15.1444 20.566 14.9555 20.4993 14.7333C20.4327 14.511 20.2771 14.3999 20.0327 14.3999H17.3993L16.466 11.6999C16.3771 11.4777 16.2216 11.3666 15.9993 11.3666C15.7771 11.3666 15.6216 11.4777 15.5327 11.6999L14.5993 14.3999H11.966C11.7216 14.3999 11.566 14.511 11.4993 14.7333C11.4327 14.9555 11.4882 15.1444 11.666 15.2999L13.866 17.1333L12.9993 19.8666C12.9327 20.111 12.9993 20.3055 13.1993 20.4499C13.3993 20.5944 13.5993 20.5888 13.7993 20.4333L15.9993 18.6999ZM4.66602 26.6666C4.11046 26.6666 3.63824 26.4721 3.24935 26.0833C2.86046 25.6944 2.66602 25.2221 2.66602 24.6666L2.66602 20.2666C2.66602 20.0444 2.72713 19.8499 2.84935 19.6833C2.97157 19.5166 3.13268 19.3999 3.33268 19.3333C4.02157 19.0888 4.56046 18.6499 4.94935 18.0166C5.33824 17.3833 5.53268 16.711 5.53268 15.9999C5.53268 15.311 5.33824 14.6444 4.94935 13.9999C4.56046 13.3555 4.02157 12.911 3.33268 12.6666C3.13268 12.5999 2.97157 12.4833 2.84935 12.3166C2.72713 12.1499 2.66602 11.9555 2.66602 11.7333L2.66602 7.33325C2.66602 6.7777 2.86046 6.30547 3.24935 5.91659C3.63824 5.5277 4.11046 5.33325 4.66602 5.33325L27.3327 5.33325C27.8882 5.33325 28.3605 5.5277 28.7493 5.91659C29.1382 6.30547 29.3327 6.7777 29.3327 7.33325V11.7333C29.3327 11.9555 29.2716 12.1499 29.1493 12.3166C29.0271 12.4833 28.866 12.5999 28.666 12.6666C27.9771 12.911 27.4382 13.3555 27.0493 13.9999C26.6605 14.6444 26.466 15.311 26.466 15.9999C26.466 16.711 26.6605 17.3833 27.0493 18.0166C27.4382 18.6499 27.9771 19.0888 28.666 19.3333C28.866 19.3999 29.0271 19.5166 29.1493 19.6833C29.2716 19.8499 29.3327 20.0444 29.3327 20.2666V24.6666C29.3327 25.2221 29.1382 25.6944 28.7493 26.0833C28.3605 26.4721 27.8882 26.6666 27.3327 26.6666L4.66602 26.6666Z" fill="#B80E80"/></svg>
                </span> */}
                <div className="booking-pathway__ticket-quantity-heading">
                  <h3 className="booking-pathway__header">How many tickets would you like?</h3>
                  {isReserved && <p>We’ll only show you available tickets where you can be seated together.</p>}
                </div>
                <TicketQuantityButtons
                  ticketQuantity={ticketQuantity}
                  setTicketQuantity={setTicketQuantity}
                />
              </div>
            </>
          )}

          <div className="booking-pathway__footer booking-pathway__select-quantity--footer">
            {isReserved ? (
              <>
                {config.HAS_BEST_AVAILABLE ? (
                  <BestAvailable setStepNumber={setStepNumber} ticketQuantity={ticketQuantity} setTicketQuantity={ticketQuantity} continueLink={props.continueLink} bandsByPrice={bandsByPrice} />
                  // <>
                  //   <BestAvailableButton
                  //     ticketQuantity={ticketQuantity}
                  //     continueLink={props.continueLink}
                  //   />
                  //   <PurchaseByPrice
                  //     bandsByPrice={bandsByPrice}
                  //     ticketQuantity={ticketQuantity}
                  //     continueLink={props.continueLink}
                  //   />
                  // </>
                ) : null}
              </>
            ) : (
              <>
                <button
                  className="btn btn--primary"
                  type="button"
                  role="link"
                  disabled={addTickets.isPending}
                  onClick={() => handleAddTickets()}
                >
                  Add to basket
                  {addTickets.isPending && <LoadingIndicator />}
                </button>                
              </>
            )}
          </div>
          

          {isReserved && <div class="booking-pathway__footer booking-pathway__select-quantity--own-choose">
              <button
                className="btn--link"
                type="button"
                onClick={async () => {
                  await clearBasket();
                  navigate(`/book-online/${instanceId}/select-seats`);
                }}
              >
                    <PreviousIcon /> Choose own seats instead
              </button>

            </div>
          }
        </div>

      </section>

      {!isReserved &&
        <>

          <SeatSelectionSummary setCheckedForPriority = {props.setCheckedForPriority} checkedForPriority={props.checkedForPriority} showType = {true} />

          <div className="booking-pathway__advisory-message">
                  {  advisory_message && 
                    <div className="booking-pathway__advisory-message-text"
                        dangerouslySetInnerHTML={{
                                    __html:advisory_message.innerHTML,
                                  }}
                    >          
                    </div>
                  }
                  <TransactionCommissionMessage/>
                </div>
          
          <PromoCodeInput />

          <div className="booking-pathway__footer">
                  <button
                    className="btn btn--primary"
                    type="button"
                    role="link"
                    onClick={() => {
                      navigate(props.continueLink);
                    }}
                    disabled={
                      basketDataFetching ||
                      addTickets.isPending ||
                      !UTILS.getTicketsByInstance(basketData?.tickets).length
                    }
                  >
                     Secure these tickets
                    {basketDataFetching || addTickets.isPending && (
                      <LoadingIndicator />
                    )}
                  </button>                  
          </div>
        </>
      }
    </div>
  );
}
